import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import { LoadingScreen } from "src/components/LoadingScreen";

export interface EnvConfig {
  authMode: "prod" | "local";
  posthogMode: "prod" | "local";
  posthogApiKey: string;
  posthogHost: string;
  // Stripe — present in both modes. `stripeBaseUrl` is only sent in local mode;
  // `stripePublishableKey` only in prod. Both fall back to empty strings so
  // the StripeProvider (added in the next commit) can construct sensibly.
  stripeMode: "prod" | "local";
  stripeBaseUrl?: string;
  stripePublishableKey?: string;
  exportPriceCents: number;
  exportPriceCurrency: string;
}

// Fallback used when /api/env is unreachable (Go backend not running during
// pure-frontend dev). Keeps the app bootable without the backend.
const DEFAULT_ENV: EnvConfig = {
  authMode: "local",
  posthogMode: "local",
  posthogApiKey: "",
  posthogHost: "",
  stripeMode: "local",
  stripeBaseUrl: "http://localhost:6975",
  exportPriceCents: 999,
  exportPriceCurrency: "eur",
};

const envContext = createContext<EnvConfig | null>(null);

export function useEnv() {
  const ctx = useContext(envContext);
  if (!ctx) throw new Error("useEnv must be used within EnvProvider");
  return ctx;
}

export function EnvProvider({ children }: { children: ReactNode }) {
  const [env, setEnv] = useState<EnvConfig | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch("/api/env");
        if (!res.ok) throw new Error(`/api/env returned ${res.status}`);
        const data = (await res.json()) as EnvConfig;
        if (!cancelled) setEnv(data);
      } catch (err) {
        console.warn("/api/env fetch failed, using defaults:", err);
        if (!cancelled) setEnv(DEFAULT_ENV);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  if (!env) return <LoadingScreen />;

  return <envContext.Provider value={env}>{children}</envContext.Provider>;
}
